import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Controller } from "react-hook-form";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";

import Dialog from "components/dialog";
import { TASK_PRIORITY_NOT_SET } from "constants/staff-tasks";
import { useStaffTaskRequiredFields } from "features/staff-tasks-v2/hooks/use-staff-task-required-fields.hook";
import { getTaskPriorityOptions } from "features/staff-tasks-v2/staff-task.utils";

type BulkUpdatePriorityModalProps = {
  isOpen: boolean;
  selectedCount: number;
  onClose: () => void;
  onSave: (priority: number) => void;
  isLoading?: boolean;
};

export default function BulkUpdatePriorityModal({
  isOpen,
  selectedCount,
  onClose,
  onSave,
  isLoading = false
}: BulkUpdatePriorityModalProps) {
  // When the practice requires a priority, "Not Set" is not offered (the backend
  // rejects update_priority=0) and nothing is pre-selected.
  const { isPriorityRequired } = useStaffTaskRequiredFields();
  const priorityOptions = useMemo(
    () => getTaskPriorityOptions(isPriorityRequired),
    [isPriorityRequired]
  );

  const { control, handleSubmit, reset } = useForm<{ priority: number | "" }>({
    defaultValues: {
      priority: isPriorityRequired ? "" : TASK_PRIORITY_NOT_SET
    }
  });

  const onSubmit = useCallback(
    (data: { priority: number | "" }) => {
      if (data.priority === "") {
        return;
      }
      onSave(data.priority);
      reset();
    },
    [onSave, reset]
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return (
    <Dialog
      title={`Update Priority for ${selectedCount} task${selectedCount !== 1 ? "s" : ""}`}
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3} sx={{ p: 2 }}>
          <Controller
            name="priority"
            control={control}
            rules={{ required: "Select a priority level" }}
            render={({ field, fieldState }) => (
              <FormControl fullWidth error={Boolean(fieldState.error)}>
                <InputLabel>Priority</InputLabel>
                <Select {...field} label="Priority">
                  {priorityOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {fieldState.error && (
                  <FormHelperText>{fieldState.error.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              Update
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
}
